import type { Control } from 'react-hook-form';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect, useContext, createContext } from 'react';

import { useParams, useSearchParams } from 'src/routes/hooks';

import { stepSchemas } from './schema';
import { useGetSupplierContractApplication } from '../hooks';

import type { StepName, CompleteApplicationState } from './schema';

interface FormContextType {
  isValid: boolean;
  trigger: () => Promise<boolean>;
  handleSubmit: any;
  currentStep: StepName;
  formState: any;
  sessionId: string;
  // Application state management
  applicationState: CompleteApplicationState;
  setApplicationState: (
    state:
      | Partial<CompleteApplicationState>
      | ((prev: CompleteApplicationState) => Partial<CompleteApplicationState>)
  ) => void;
  resetApplicationState: () => void;
  register: any;
  watch: any;
  setValue: any;
  reset: any;
  control: Control<any>;
}

const FormContext = createContext<FormContextType | null>(null);

export const useSubmitApplicationFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useSubmitApplicationFormContext must be used within FormProvider');
  }
  return context;
};

interface FormProviderProps {
  children: React.ReactNode;
}

const initialApplicationState: CompleteApplicationState = {
  commitToActivities: [],
  procurerInterestedActivities: [],
  submissionQuestionsAnswers: [],
  contract: {},
  contractLeadName: '',
  contractLeadEmail: '',
  socialValueLeadName: '',
  socialValueLeadEmail: '',
  privacyPolicyAccepted: false,
  privacyPolicyRequired: false,
  privacyPolicy: null,
  generalError: '',
};

export default function FormProvider({ children }: FormProviderProps) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [sessionId] = useState(() => crypto.randomUUID());
  const [applicationState, _setApplicationState] =
    useState<CompleteApplicationState>(initialApplicationState);

  const { data = {} } = useGetSupplierContractApplication({
    applicationId: Number(id),
    enabled: !!id,
  });

  const currentStep = (searchParams.get('step') || 'introduction') as StepName;
  const currentSchema = stepSchemas[currentStep];
  const methods = useForm<any>({
    resolver: zodResolver(currentSchema),
    mode: 'onChange',
    defaultValues: initialApplicationState,
  });
  const { formState, trigger, handleSubmit, register, reset, watch, setValue, control } = methods;

  useEffect(() => {
    if (data) {
      const qualitativeQuestions = data.contract?.qualitativeQuestions || [];
      const submissionQuestionsAnswers = data.submissionQuestionsAnswers || [];
      const mergedQualitativeQuestions = qualitativeQuestions.map((question: any) => ({
        ...question,
        answer: submissionQuestionsAnswers.find((answer: any) => {
          if (!answer.id) {
            return answer.title === question.title;
          }
          return answer.id === question.id;
        })?.answer,
      }));

      const nextState = {
        ...data,
        submissionQuestionsAnswers: mergedQualitativeQuestions,
      };
      _setApplicationState(nextState);
      reset(nextState);
    }
  }, [data, reset]);

  const resetApplicationState = () => {
    _setApplicationState(initialApplicationState);
  };

  const setApplicationState = (
    state:
      | Partial<CompleteApplicationState>
      | ((prev: CompleteApplicationState) => Partial<CompleteApplicationState>)
  ) => {
    _setApplicationState((prev) => ({
      ...prev,
      ...(typeof state === 'function' ? state(prev) : state),
    }));
  };

  const contextValue: FormContextType = {
    isValid: formState.isValid,
    currentStep,
    sessionId,
    applicationState,
    setApplicationState,
    resetApplicationState,
    trigger,
    handleSubmit,
    formState,
    register,
    watch,
    reset,
    setValue,
    control,
  };

  return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>;
}
